import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Lenis from "lenis";
import { motion, AnimatePresence } from "framer-motion";
import { 
  TrendingUp, BarChart3, LineChart, Target, 
  ArrowRight, ShieldCheck, Cpu, CheckCircle2, HelpCircle
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Process from "../components/Process";
import SEO from "../components/SEO";
import SectionStepper from "../components/SectionStepper";
import GameInstructionsModal from "../components/ui/GameInstructionsModal";

const marketingSteps = [
  {
    step: "01",
    title: "funnel audit",
    desc: "Mapping out your historical traffic, attribution models, and landing pages to detect drop-off vectors.",
    image: "https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&q=80&w=1200"
  },
  {
    step: "02",
    title: "audience modeling",
    desc: "Building high-intent custom lists, search intent clusters, and lookalikes based on first-party data.",
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=1200"
  },
  {
    step: "03",
    title: "creative sprint",
    desc: "Scripting, filming, and animating premium ad hooks and visual patterns designed to stop the scroll.",
    image: "https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?auto=format&fit=crop&q=80&w=1200"
  },
  {
    step: "04",
    title: "smart scaling",
    desc: "Deploying campaigns with smart bidding, monitoring conversion events, and scaling budget on winners.",
    image: "https://images.pexels.com/photos/590022/pexels-photo-590022.jpeg?auto=compress&cs=tinysrgb&w=1200"
  }
];

const PerformanceMarketing = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true
    });
    const raf = (time) => {
      lenis.raf(time);
      requestAnimationFrame(raf);
    };
    requestAnimationFrame(raf);
    return () => lenis.destroy();
  }, []);

  const stepperSections = [
    { id: "hero", label: "Scale" },
    { id: "capabilities", label: "Funnels" },
    { id: "meta", label: "Meta Ads" },
    { id: "google", label: "Google Ads" },
    { id: "process", label: "Strategy" },
    { id: "workshop", label: "ROAS Lab" }
  ];

  return (
    <div className="bg-[var(--ink)] text-[var(--cream)] min-h-screen">
      <SEO 
        title="Performance Marketing & Customer Acquisition" 
        description="Autopilot customer acquisition through dynamic Meta Ads and high-intent Google Ads strategies built for ROI."
      />
      <SectionStepper sections={stepperSections} />
      <div className="grain" />
      <Navbar />

      <main>
        {/* HERO SECTION */}
        <section id="hero" className="relative min-h-screen flex items-center pt-20 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <div className="absolute inset-0 bg-gradient-to-b from-[var(--ink)] via-transparent to-[var(--ink)] z-10" />
            <motion.div
              initial={{ scale: 1.1, opacity: 0 }}
              animate={{ scale: 1, opacity: 0.45 }}
              transition={{ duration: 1.5 }}
              className="w-full h-full"
            >
              <video 
                autoPlay 
                muted 
                loop 
                playsInline 
                preload="auto"
                className="w-full h-full object-cover grayscale brightness-50"
              >
                <source src="https://videos.pexels.com/video-files/3130182/3130182-uhd_2560_1440_30fps.mp4" type="video/mp4" />
              </video>
            </motion.div>
          </div>

          <div className="container-xl w-full relative z-20 px-6">
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="max-w-5xl"
            >
              <span className="font-impact text-[var(--gold)] tracking-[0.4em] uppercase text-sm mb-6 block">
                Acquisition Excellence
              </span>
              <h1 className="font-display text-6xl md:text-8xl lg:text-9xl mb-8 leading-tight">
                Growth that <br />
                <span className="italic font-serif-accent gold-text">Multiplies</span>
              </h1>
              <p className="max-w-2xl text-[var(--cream-muted)] text-lg md:text-xl mb-12 font-body leading-relaxed">
                Autopilot growth engineered with scientific precision. We combine cinematic ad creative with data-driven funnel optimization to deliver compound returns on ad spend.
              </p>
              <div className="flex flex-col md:flex-row items-start justify-start gap-6">
                <Link to="/contact" className="btn-primary">
                  Scale Your Brand <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
                <a href="#workshop" className="btn-ghost">
                  Enter ROAS Lab
                </a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* CAPABILITIES SECTION */}
        <section id="capabilities" className="py-32 relative">
          <div className="container-xl px-6">
            <Kicker index="01" label="funnel metrics" />
            <h2 className="mt-8 font-display text-4xl md:text-6xl text-[var(--cream)] mb-20">
              Data-Led <span className="italic gold-text">Autopilot</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { icon: <Target />, title: "Funnel Architecture", desc: "Crafting optimized buyer journeys from cold impressions to lifetime customer loyalty." },
                { icon: <LineChart />, title: "Predictive Analytics", desc: "Scientific attribution modeling and real-time budgeting to double down on winning segments." },
                { icon: <BarChart3 />, title: "Creative Sprints", desc: "High-hook video and graphic ad variations crafted to stop feed scrolling and convert clicks." }
              ].map((s, i) => (
                <div key={i} className="p-10 bg-[var(--ink-2)] border border-[var(--rule)] rounded-2xl hover:border-[var(--gold)] transition-all duration-500 group">
                  <div className="text-[var(--gold)] mb-6 transform group-hover:scale-110 transition-transform origin-left">{s.icon}</div>
                  <h3 className="font-display text-2xl mb-4">{s.title}</h3>
                  <p className="text-[var(--muted)] text-sm leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* META ADS SECTION (Alternating showcase) */}
        <section id="meta" className="py-32 relative border-t border-[var(--rule)]">
          <div className="container-xl px-6">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <Kicker index="02" label="META & INSTAGRAM ADS" />
                <h2 className="mt-8 font-display text-4xl md:text-6xl mb-8 leading-tight">
                  Visual <br /><span className="italic gold-text">Omnipresence</span>
                </h2>
                <p className="text-[var(--cream-muted)] text-lg leading-relaxed mb-8">
                  Meta Ads are won in the creative suite. We produce thumb-stopping cinematic assets designed specifically to fit standard social formats while triggering emotional purchase behaviors.
                </p>
                <ul className="space-y-4 text-sm text-[var(--muted)] mb-8 font-body">
                  <li className="flex items-center gap-3"><span className="w-1.5 h-1.5 rounded-full bg-[var(--gold)]" /> Custom CBO/ABO budget structures built for scale</li>
                  <li className="flex items-center gap-3"><span className="w-1.5 h-1.5 rounded-full bg-[var(--gold)]" /> Broad audience targeting and dynamic lookalike clusters</li>
                  <li className="flex items-center gap-3"><span className="w-1.5 h-1.5 rounded-full bg-[var(--gold)]" /> First-party pixel optimization to navigate post-iOS14 limits</li>
                </ul>
                <Link to="/contact" className="btn-ghost scale-90 md:scale-100 origin-left">
                  Request Meta Strategy
                </Link>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="relative rounded-[32px] overflow-hidden border border-[var(--rule)] shadow-2xl aspect-[4/3] bg-black"
              >
                <img 
                  src="/images/meta_ads.jpeg" 
                  alt="Meta Performance Ads Architecture"
                  className="w-full h-full object-cover filter brightness-95 grayscale hover:grayscale-0 transition-all duration-700" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--ink)]/40 to-transparent" />
                <div className="absolute bottom-4 right-4 bg-black/60 backdrop-blur-md px-3 py-1 rounded text-[8px] font-impact tracking-widest text-[var(--gold)] uppercase border border-white/10">
                  REAL ACQUISITION HUD
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* GOOGLE ADS SECTION (Alternating showcase) */}
        <section id="google" className="py-32 relative border-t border-[var(--rule)] bg-[#050505]">
          <div className="container-xl px-6">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="relative rounded-[32px] overflow-hidden border border-[var(--rule)] shadow-2xl aspect-[4/3] bg-black order-2 lg:order-1"
              >
                <img 
                  src="/images/google_ads.jpeg" 
                  alt="Google Ads High Intent Keywords Strategy"
                  className="w-full h-full object-cover filter brightness-95 grayscale hover:grayscale-0 transition-all duration-700" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--ink)]/40 to-transparent" />
                <div className="absolute bottom-4 right-4 bg-black/60 backdrop-blur-md px-3 py-1 rounded text-[8px] font-impact tracking-widest text-[var(--gold)] uppercase border border-white/10">
                  KEYWORD INTENT INTERFACE
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="order-1 lg:order-2"
              >
                <Kicker index="03" label="GOOGLE & YOUTUBE SEARCH" />
                <h2 className="mt-8 font-display text-4xl md:text-6xl mb-8 leading-tight">
                  Precision <br /><span className="italic gold-text">Harvesting</span>
                </h2>
                <p className="text-[var(--cream-muted)] text-lg leading-relaxed mb-8">
                  While social builds brand desire, Google captures organic search intent. We optimize search campaigns to catch high-intent prospects exactly at their point of decision-making.
                </p>
                <ul className="space-y-4 text-sm text-[var(--muted)] mb-8 font-body">
                  <li className="flex items-center gap-3"><span className="w-1.5 h-1.5 rounded-full bg-[var(--gold)]" /> Precision Search ads leveraging exact and phrase matches</li>
                  <li className="flex items-center gap-3"><span className="w-1.5 h-1.5 rounded-full bg-[var(--gold)]" /> Multi-asset Performance Max (PMax) channel optimization</li>
                  <li className="flex items-center gap-3"><span className="w-1.5 h-1.5 rounded-full bg-[var(--gold)]" /> Bidding automation focusing strictly on target CPA or tROAS</li>
                </ul>
                <Link to="/contact" className="btn-ghost scale-90 md:scale-100 origin-left">
                  Request Google Audit
                </Link>
              </motion.div>
            </div>
          </div>
        </section>

        {/* THE METHOD (PROCESS SECTION) */}
        <Process 
          id="process" 
          steps={marketingSteps} 
          title="The Scaling Blueprint" 
          kicker={{ index: "04", label: "Execution" }} 
        />

        {/* ROAS CAMPAIGN OPTIMIZER LAB (THE GAME) */}
        <ROASWorkshop id="workshop" />
      </main>

      <Footer />
    </div>
  );
};

/* --- HIGH-TECH ROAS OPTIMIZER LAB GAME COMPONENT --- */
const ROASWorkshop = ({ id }) => {
  const [gameState, setGameState] = useState("start");
  const [params, setParams] = useState({ creative: 50, keywords: 50, bidding: 50 });
  const [roas, setRoas] = useState(1.8);
  const [showInstructions, setShowInstructions] = useState(false);

  useEffect(() => {
    // Campaign simulator math: optimal points generate 10.0x ROAS
    const baseRoas = 1.0;
    const bonus = (params.creative * 0.45) + (params.keywords * 0.35) + (params.bidding * 0.20);
    const newRoas = Math.max(0.8, (baseRoas + (bonus / 10))).toFixed(1);
    setRoas(parseFloat(newRoas));

    if (gameState === "playing" && parseFloat(newRoas) >= 10.0) {
      setGameState("success");
    }
  }, [params, gameState]);

  const initGame = () => {
    setParams({ creative: 50, keywords: 50, bidding: 50 });
    setGameState("playing");
  };

  return (
    <section id={id} className="py-24 relative overflow-hidden bg-[#050505] border-t border-[var(--rule)]">
      <div className="container-xl px-6">
        <div className="flex flex-col mb-12">
          <div className="text-left mb-8">
            <Kicker index="LAB-01" label="Campaign Lab" />
          </div>
          <div className="text-center">
            <h2 className="mt-4 font-display text-4xl md:text-5xl text-[var(--cream)]">
              ROAS <span className="italic gold-text">Optimizer</span>
            </h2>
            <p className="mt-4 text-[var(--cream-muted)] text-sm uppercase tracking-[0.2em] max-w-lg mx-auto">
              Tune your customer funnel vectors to hit a legendary 10x return on ad spend.
            </p>
            <button 
              onClick={() => setShowInstructions(true)}
              className="mt-6 flex items-center gap-2 text-[var(--gold)]/60 hover:text-[var(--gold)] transition-colors mx-auto text-[10px] font-impact tracking-widest uppercase"
            >
              <HelpCircle size={14} />
              How to Play
            </button>
          </div>
        </div>

        <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-12 items-center bg-[#0d0d0d] p-8 md:p-16 rounded-[40px] border border-[var(--rule)] relative shadow-2xl">
          
          {/* Performance Monitor Screen */}
          <div className="relative aspect-[4/3] md:aspect-video bg-black rounded-3xl overflow-hidden border border-[var(--rule)] flex flex-col items-center justify-center p-12">
            <div className="absolute top-4 left-6 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-[10px] text-green-500 font-mono uppercase tracking-widest">Pixel Receiving Data</span>
            </div>

            <motion.div 
              animate={{ scale: roas >= 10.0 ? 1.08 : 1 }}
              className={`text-7xl md:text-9xl font-impact transition-colors duration-500 ${roas >= 10.0 ? "text-[var(--gold)]" : "text-white"}`}
            >
              {roas}x
            </motion.div>
            <div className="text-[var(--muted)] font-display italic text-xl mt-4">Simulated ROAS</div>

            {/* Target goal indicator */}
            <div className="absolute bottom-6 inset-x-12 flex flex-col items-center gap-2">
              <div className="w-full h-1 bg-white/5 rounded-full overflow-hidden">
                <motion.div 
                  animate={{ width: `${Math.min(100, roas * 10)}%` }}
                  className="h-full bg-[var(--gold)]" 
                />
              </div>
              <span className="text-[8px] uppercase tracking-widest text-[var(--gold)] font-impact">Target ROI Goal: 10.0x ROAS</span>
            </div>
          </div>

          {/* Sliders Control Panel */}
          <div className="relative z-40">
            {gameState === "start" ? (
              <div className="text-center py-10 flex flex-col items-center">
                <Cpu className="w-16 h-16 text-[var(--gold)] mb-8 opacity-50" />
                <h3 className="font-display text-3xl text-white mb-8 italic">Calibrate Funnel?</h3>
                <button 
                  className="btn-primary px-12"
                  onClick={initGame}
                >
                  Enter Workshop
                </button>
              </div>
            ) : (
              <div className="space-y-12">
                <SliderControl label="Ad Hook Creative Quality" value={params.creative} onChange={(v) => setParams({...params, creative: v})} />
                <SliderControl label="Keyword Match Precision" value={params.keywords} onChange={(v) => setParams({...params, keywords: v})} />
                <SliderControl label="Audience Match-Rate & Bid Cap" value={params.bidding} onChange={(v) => setParams({...params, bidding: v})} />
                
                <div className="p-6 bg-white/5 border border-white/10 rounded-2xl flex items-center gap-4">
                  <ShieldCheck className="text-[var(--gold)]" size={24} />
                  <div className="text-xs text-[var(--muted)] leading-relaxed">
                    Maximizing ad hooks (Meta Ads priority) while refining match intent (Google Ads priority) leads to visual scalability.
                  </div>
                </div>
              </div>
            )}
          </div>

          <AnimatePresence>
            {gameState === "success" && (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-black/95 backdrop-blur-xl z-50 rounded-[40px] flex flex-col items-center justify-center p-12 text-center"
              >
                <CheckCircle2 className="text-[var(--gold)] w-20 h-20 mb-8" />
                <h3 className="font-display text-5xl text-white mb-4">CAMPAIGN <span className="italic gold-text">SUCCESS</span></h3>
                <p className="text-[var(--cream-muted)] mb-10">You have scaled customer acquisition to peak efficiency and ROAS!</p>
                <button className="btn-primary px-12" onClick={initGame}>Scale Another Funnel</button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <GameInstructionsModal 
        isOpen={showInstructions}
        onClose={() => setShowInstructions(false)}
        title="ROAS Campaign Lab"
        instructions={[
          { title: "Optimize ROAS Output", desc: "The monitor displays simulated Campaign Return on Ad Spend (ROAS). Your ultimate objective is to scale this value to exactly 10.0x." },
          { title: "Slide Key parameters", desc: "Tweak Hook Creative Quality, Keyword Intent Match, and Audience targeting precision metrics to discover compounding conversions." },
          { title: "Understand Channel Weight", desc: "Creative Quality is crucial for viral Meta Ads, while Keyword Match accuracy defines high-conversion Google Ads performance." },
          { title: "Scale and Lock ROI", desc: "Achieving a perfect 10.0x ROAS locks the campaign into permanent autopilot scale. Try the challenge again with a fresh run!" }
        ]}
      />
    </section>
  );
};

const SliderControl = ({ label, value, onChange }) => (
  <div className="space-y-4">
    <div className="flex justify-between items-end">
      <label className="text-[10px] uppercase tracking-[0.3em] text-[var(--gold)] font-impact">{label}</label>
      <span className="text-xl font-display text-white">{value}%</span>
    </div>
    <input 
      type="range" min="0" max="100" value={value}
      onChange={(e) => onChange(parseInt(e.target.value))}
      className="w-full h-1 bg-[var(--rule)] appearance-none cursor-pointer accent-[var(--gold)] rounded-full outline-none"
    />
  </div>
);

const Kicker = ({ index, label }) => (
  <div className="flex items-center gap-4 mb-4">
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--gold)]">{index}</span>
    <span className="w-16 h-px bg-[var(--rule)]" />
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--cream-muted)] uppercase">{label}</span>
  </div>
);

export default PerformanceMarketing;
